import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Layer } from '../../state/types';

type SortKey = 'category' | 'group' | 'name' | 'description';

interface LayersTableProps {
  layers: Layer[];
  loading: boolean;
  error?: string | null;
  onRefresh: () => void;
}

const headers: { label: string; key: SortKey }[] = [
  { label: 'Category', key: 'category' },
  { label: 'Group', key: 'group' },
  { label: 'Name', key: 'name' },
  { label: 'Description', key: 'description' },
];

const layerKey = (layer: Layer) => `${layer.category}#${layer.group}#${layer.name}`;

const LayerThumbnail: React.FC<{ layer: Layer; onClick?: () => void }> = ({ layer, onClick }) => {
  const [errored, setErrored] = useState(false);

  if (!layer.imageUrl || errored) {
    return (
      <Avatar
        variant="rounded"
        sx={{ width: 48, height: 48, bgcolor: 'action.hover', color: 'text.disabled' }}
        data-cy="layer-thumb-missing"
      >
        <BrokenImageIcon fontSize="small" />
      </Avatar>
    );
  }

  return (
    <Avatar
      variant="rounded"
      src={layer.imageUrl}
      alt={layer.name}
      onClick={onClick}
      onError={() => setErrored(true)}
      imgProps={{ loading: 'lazy' }}
      sx={{
        width: 48,
        height: 48,
        cursor: onClick ? 'pointer' : 'default',
        bgcolor: 'action.hover',
        '& img': { objectFit: 'contain' },
      }}
      data-cy="layer-thumb"
    />
  );
};

const LayersTable: React.FC<LayersTableProps> = ({ layers, loading, error, onRefresh }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('category');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [previewLayer, setPreviewLayer] = useState<Layer | null>(null);

  const categories = useMemo(
    () => Array.from(new Set(layers.map((l) => l.category).filter(Boolean))).sort(),
    [layers]
  );

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  const filteredAndSorted = useMemo(() => {
    const query = searchTerm.toLowerCase();
    const filtered = layers.filter((layer) => {
      if (categoryFilter && layer.category !== categoryFilter) return false;
      if (!query) return true;
      return (
        layer.category?.toLowerCase().includes(query) ||
        layer.group?.toLowerCase().includes(query) ||
        layer.name?.toLowerCase().includes(query) ||
        layer.description?.toLowerCase().includes(query)
      );
    });

    return [...filtered].sort((a, b) => {
      const aVal = (a[sortBy] || '').toString();
      const bVal = (b[sortBy] || '').toString();
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [layers, searchTerm, categoryFilter, sortBy, sortDirection]);

  const total = filteredAndSorted.length;
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const paginated = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredAndSorted.slice(start, start + rowsPerPage);
  }, [filteredAndSorted, page, rowsPerPage]);

  const startItem = total > 0 ? (page - 1) * rowsPerPage + 1 : 0;
  const endItem = Math.min(page * rowsPerPage, total);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ mb: 2, flexShrink: 0 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              label="Search"
              placeholder="Category, group, name, description"
              fullWidth
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              data-cy="layers-search-input"
            />
          </Grid>
          <Grid item xs={12} sm={4} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel id="layers-category-filter-label">Category</InputLabel>
              <Select
                labelId="layers-category-filter-label"
                label="Category"
                value={categoryFilter}
                onChange={(e) => {
                  setCategoryFilter(e.target.value);
                  setPage(1);
                }}
                data-cy="layers-category-filter"
              >
                <MenuItem value="">All categories</MenuItem>
                {categories.map((cat) => (
                  <MenuItem key={cat} value={cat}>
                    {cat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2} md={3}>
            <Button
              variant="outlined"
              fullWidth
              startIcon={<RefreshIcon />}
              onClick={onRefresh}
              disabled={loading}
              data-cy="layers-refresh-btn"
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} data-cy="layers-error">
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }} data-cy="layers-loading">
          <CircularProgress />
        </Box>
      ) : total === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }} data-cy="layers-empty">
          <Typography color="text.secondary">No layers found.</Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ flex: 1 }}>
          {paginated.map((layer) => (
            <Card key={layerKey(layer)} sx={{ mb: 1.5 }} data-cy="layers-card">
              <CardContent>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <LayerThumbnail layer={layer} onClick={() => setPreviewLayer(layer)} />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap>
                      {layer.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                      <Chip label={layer.category} size="small" />
                      <Chip label={layer.group} size="small" variant="outlined" />
                    </Box>
                  </Box>
                </Box>
                {layer.description ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {layer.description}
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          sx={{ maxHeight: '48vh', overflow: 'auto', maxWidth: '100%' }}
        >
          <Table sx={{ minWidth: 800 }}>
            <TableHead
              sx={{
                position: 'sticky',
                top: 0,
                backgroundColor: theme.palette.background.paper,
                zIndex: 1,
              }}
            >
              <TableRow>
                <TableCell sx={{ width: 72 }}>Image</TableCell>
                {headers.map(({ label, key }) => (
                  <TableCell key={key}>
                    <TableSortLabel
                      active={sortBy === key}
                      direction={sortBy === key ? sortDirection : 'asc'}
                      onClick={() => handleSort(key)}
                      data-cy={`layers-sort-${key}`}
                    >
                      {label}
                    </TableSortLabel>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((layer) => (
                <TableRow key={layerKey(layer)} hover data-cy="layers-table-row">
                  <TableCell>
                    <LayerThumbnail layer={layer} onClick={() => setPreviewLayer(layer)} />
                  </TableCell>
                  <TableCell>
                    <Chip label={layer.category} size="small" />
                  </TableCell>
                  <TableCell>
                    <Typography>{layer.group}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography>{layer.name}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {layer.description || '--'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && total > 0 ? (
        <Box
          sx={{
            mt: 2,
            flexShrink: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 1.5 : 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', order: isMobile ? 3 : 1 }}>
            <Typography sx={{ mr: 1 }}>Rows:</Typography>
            <Select
              data-cy="layers-pagination-rows-select"
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(1);
              }}
              size="small"
              sx={{ height: '30px' }}
            >
              {[10, 25, 50, 100].map((value) => (
                <MenuItem key={value} value={value}>
                  {value}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Typography data-cy="layers-pagination-total-text" sx={{ fontWeight: 500, order: 2 }}>
            {startItem}-{endItem} of {total.toLocaleString()} Layers
          </Typography>

          <Pagination
            data-cy="layers-pagination-control"
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            size={isMobile ? 'small' : 'medium'}
            siblingCount={isMobile ? 1 : 2}
            sx={{ order: isMobile ? 1 : 3 }}
          />
        </Box>
      ) : null}

      <Dialog
        open={!!previewLayer}
        onClose={() => setPreviewLayer(null)}
        maxWidth="md"
        data-cy="layers-preview-dialog"
      >
        <DialogContent>
          {previewLayer ? (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h6" gutterBottom>
                {previewLayer.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {previewLayer.category} / {previewLayer.group}
              </Typography>
              {previewLayer.imageUrl ? (
                <Box
                  component="img"
                  src={previewLayer.imageUrl}
                  alt={previewLayer.name}
                  sx={{ maxWidth: '100%', maxHeight: '70vh', mt: 1 }}
                />
              ) : (
                <Typography color="text.secondary" sx={{ py: 4 }}>
                  No image available for this layer.
                </Typography>
              )}
              {previewLayer.description ? (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  {previewLayer.description}
                </Typography>
              ) : null}
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default LayersTable;
